import { FC, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'

import { LoadingSpinner } from '../../../../@paco/components'
import { transformBaseScheduleShiftToViewModel } from '../../../../@paco/entities/BaseScheduleShift/BaseScheduleShiftTransformers'
import { startOfYear } from '../../../../@paco/helpers/date'
import checkPermission from '../../../../@paco/helpers/permissions/checkPermission'
import {
    deleteBaseScheduleShift,
    editBaseScheduleShift,
    fetchBaseScheduleShift,
} from '../../../../@paco/redux/baseScheduleShift/baseScheduleShiftActions'
import { setIsSuccessful } from '../../../../@paco/redux/baseScheduleShift/baseScheduleShiftReducer'
import {
    deleteBaseScheduleShiftPlanning,
    editBaseScheduleShiftPlanning,
    fetchEmployeeOptions,
    planUserToBaseScheduleShift,
    setBaseScheduleShiftPlanningColor,
} from '../../../../@paco/redux/baseScheduleShiftPlanning/baseScheduleShiftPlanningActions'
import { useTypedDispatch, useTypedSelector } from '../../../../@paco/redux/store'
import { ScheduleRoutes } from '../../../../@paco/routes/helpers'
import { PrivateRouteGroupRoutes } from '../../../../@paco/routes/routes'

import addDatesToBaseScheduleShift from '../../../../entities/BaseScheduleShift/helpers/addDatesToBaseScheduleShift'
import { getDaysOfWeek } from '../../../../helpers/date'
import { translate } from '../../../../helpers/translations/translator'
import { EditShiftFormData, EditShiftPlanningFormData, ShiftPlanningViewModel } from '../../../../models'
import ErrorPage from '../../../ErrorPage'
import ShiftDetail from '../../containers/ShiftDetail/ShiftDetail'


const ConnectedBaseScheduleShiftDetail: FC = () => {
    const navigate = useNavigate()
    const { id: baseScheduleShiftId } = useParams<{ id: string }>()
    const dispatch = useTypedDispatch()

    const {
        isLoading,
        isSuccessful,
        isDeleteBaseScheduleShiftSuccessful,
        baseScheduleShift: baseScheduleShiftDetail,
    } = useTypedSelector(state => state.pacoBaseScheduleShiftReducer)
    const { isLoading: isLoadingPlanning } = useTypedSelector(state => state.pacoBaseScheduleShiftPlanningReducer)
    const { permissions } = useTypedSelector(state => state.authenticatedUserReducer)

    const canDeleteBaseScheduleShifts = checkPermission(permissions, 'delete-all-base-schedule-shifts')
    const canAddBaseScheduleShiftPlannings = checkPermission(permissions, 'add-new-base-schedule-shift-plannings')
    const canEditBaseScheduleShiftPlannings = checkPermission(permissions, 'edit-all-base-schedule-shift-plannings')
    const canDeleteBaseScheduleShiftPlannings = checkPermission(permissions, 'delete-all-base-schedule-shift-plannings')

    const baseScheduleShift = useMemo(
        () => (baseScheduleShiftDetail ? transformBaseScheduleShiftToViewModel(baseScheduleShiftDetail) : undefined),
        [baseScheduleShiftDetail],
    )

    const selectedDays = useMemo(() => getDaysOfWeek(startOfYear(new Date())), [])
    const shiftWithDates = useMemo(
        () => (baseScheduleShift ? addDatesToBaseScheduleShift(baseScheduleShift, selectedDays) : undefined),
        [baseScheduleShift, selectedDays],
    )

    const handleEmployeeSelect = (userId: string) => {
        if (baseScheduleShiftDetail) {
            dispatch(planUserToBaseScheduleShift(baseScheduleShiftDetail, userId))
        }
    }

    const handleShiftPlanningColorClick = (shiftPlanning: ShiftPlanningViewModel, color?: string) => {
        if (baseScheduleShiftDetail) {
            dispatch(setBaseScheduleShiftPlanningColor(baseScheduleShiftDetail, shiftPlanning, color))
        }
    }

    const handleDeleteShiftPlanningClick = (shiftPlanning: ShiftPlanningViewModel) => {
        if (baseScheduleShiftDetail) {
            dispatch(deleteBaseScheduleShiftPlanning(baseScheduleShiftDetail, shiftPlanning.id))
        }
    }

    const handleSubmitEditShiftPlanning = (formData: EditShiftPlanningFormData, shiftPlanning: ShiftPlanningViewModel) => {
        if (baseScheduleShiftDetail) {
            dispatch(editBaseScheduleShiftPlanning(baseScheduleShiftDetail, shiftPlanning.id, formData))
        }
    }

    const handleDeleteButtonClick = () => {
        if (baseScheduleShift) {
            dispatch(deleteBaseScheduleShift(baseScheduleShift.id))
        }
    }

    const handleSave = (formData: EditShiftFormData) => {
        if (baseScheduleShift) {
            dispatch(editBaseScheduleShift(baseScheduleShift.id, formData))
        }
    }

    useEffect(() => {
        if (baseScheduleShiftDetail?.department && baseScheduleShiftDetail.period.start) {
            dispatch(fetchEmployeeOptions(baseScheduleShiftDetail.department.id))
        }
    }, [baseScheduleShiftDetail])

    useEffect(() => {
        if (baseScheduleShiftId) {
            dispatch(fetchBaseScheduleShift(baseScheduleShiftId))
        }
    }, [baseScheduleShiftId])

    useEffect(() => {
        if (baseScheduleShiftId && (isSuccessful || isDeleteBaseScheduleShiftSuccessful)) {
            dispatch(setIsSuccessful(false))

            if (baseScheduleShiftDetail?.baseScheduleId) {
                navigate(`${PrivateRouteGroupRoutes.shifts()}/${ScheduleRoutes.baseSchedule(baseScheduleShiftDetail.baseScheduleId)}`)
            } else {
                navigate(`${PrivateRouteGroupRoutes.shifts()}/${ScheduleRoutes.baseSchedules()}`)
            }
        }
    }, [isSuccessful, isDeleteBaseScheduleShiftSuccessful])

    if (! shiftWithDates && isLoading) {
        return (
            <LoadingSpinner />
        )
    }

    if (! shiftWithDates) {
        return (
            <ErrorPage errorCode={404} />
        )
    }

    return (
        <ShiftDetail
            isLoading={isLoading}
            isBaseScheduleShift
            isEmployeeLoading={isLoadingPlanning}
            canDeleteShift={canDeleteBaseScheduleShifts}
            canAddShiftPlannings={canAddBaseScheduleShiftPlannings}
            canEditShiftPlannings={canEditBaseScheduleShiftPlannings}
            canDeleteShiftPlannings={canDeleteBaseScheduleShiftPlannings}
            title={translate('common.editEntity', { entity: translate('pages.shifts.baseScheduleShift') })}
            saveButtonTitle={translate('common.save')}
            selectedDays={selectedDays}
            shift={shiftWithDates}
            onEmployeeSelect={handleEmployeeSelect}
            onShiftPlanningColorClick={handleShiftPlanningColorClick}
            onDeleteShiftPlanningClick={handleDeleteShiftPlanningClick}
            onSubmitEditShiftPlanning={handleSubmitEditShiftPlanning}
            onDeleteButtonClick={handleDeleteButtonClick}
            onSaveButtonClick={handleSave}
            className="base-schedule__shift-detail"
        />
    )
}

export default ConnectedBaseScheduleShiftDetail
